import { useEffect, useState, useRef } from 'react'
import { useSelector } from 'react-redux'
import { studioAxiosInstance } from '../processes/api/instances'
import { getConfigInfo } from '../utils/functions/playcanvas/getConfigInfo'
import { getCamera, getCurrentTemplatePresets, getActiveEnvironmentId } from '../redux/reducers/ui/uiSelectors'
import { useAppDispatch } from '../redux/store'
import { restoreTemplateThunk } from '../redux/reducers/ui/uiThunks'

export interface CameraPreset {
    id: string
    label: string
    config?: any
}

const LOCAL_PRESETS_KEY = 'camera-presets-local'

/**
 * Hook to load, manage, and update camera presets.
 * @param templateId Template identifier for presets API.
 */
export function useCameraPresets(templateId?: number) {
    const [presets, setPresets] = useState<CameraPreset[]>([])
    const [activePreset, setActivePreset] = useState<CameraPreset | null>(null)
    const dispatch = useAppDispatch()

    // Get static camera settings from Redux
    const cameraConfig = useSelector(getCamera)
    // Get template presets from Redux
    const templatePresets = useSelector(getCurrentTemplatePresets)

    // Clear all camera presets when environment changes
    const activeEnvironmentId = useSelector(getActiveEnvironmentId())
    const prevEnvRef = useRef<number>(activeEnvironmentId)

    useEffect(() => {
        if (prevEnvRef.current !== activeEnvironmentId) {
            presets.forEach(preset => {
                deletePreset(preset.id)
            })
        }
        prevEnvRef.current = activeEnvironmentId
    }, [activeEnvironmentId])

    const saveLocalPresets = (items: CameraPreset[]) => {
        try {
            localStorage.setItem(LOCAL_PRESETS_KEY, JSON.stringify(items))
        } catch (e) {
            console.log('Failed to save local presets', e)
        }
    }

    const loadPresets = async () => {
        const localPresets = getLocalPresets()

        const combinedPresets = [...localPresets, ...templatePresets]
        setPresets(combinedPresets)
        if (combinedPresets.length) setActivePreset(combinedPresets[0])
    }

    const getLocalPresets = (): CameraPreset[] => {
        try {
            const stored = localStorage.getItem(LOCAL_PRESETS_KEY)
            return stored ? JSON.parse(stored) : []
        } catch (e) {
            console.log('Failed to load local presets', e)
            return []
        }
    }

    useEffect(() => {
        loadPresets()
    }, [templateId, templatePresets])

    const deletePreset = async (presetId: string) => {
        try {
            const localPresets = getLocalPresets()
            const isLocalPreset = localPresets.some(p => p.id === presetId)

            if (isLocalPreset) {
                // Delete local preset
                const updatedLocalPresets = localPresets.filter(p => p.id !== presetId)
                saveLocalPresets(updatedLocalPresets)
                await loadPresets()
            } else {
                // Delete server preset
                await studioAxiosInstance.delete(`cameraPresets/${presetId}`)
                // Re-fetch server presets via existing restoreTemplateThunk
                if (templateId) {
                    await dispatch(restoreTemplateThunk(templateId)).unwrap()
                }
                await loadPresets()
            }

            if (activePreset?.id === presetId) {
                const remainingPresets = presets.filter(p => p.id !== presetId)
                setActivePreset(remainingPresets.length > 0 ? remainingPresets[0] : null)
            }
        } catch (e) {
            console.log('Failed to delete preset', e)
        }
    }

    const savePreset = async (customName?: string, forceLocal?: boolean) => {
        try {
            // Gather live camera transform data including rotation
            const configString = await getConfigInfo()
            const configArray = JSON.parse(configString)
            const cameraInfo = configArray.find((item: any) => item.id === 'camera')
            if (!cameraInfo) throw new Error('Camera info not found')
            const { pos, forward, up, fov, rot } = cameraInfo

            // Convert rot (x,y,z) to rotation (pitch,yaw,roll) format
            const rotation = {
                pitch: rot.x,
                yaw: rot.y,
                roll: rot.z
            }

            // Merge Redux camera settings and embed live transform (position + rotation)
            const { transform: reduxTransform, ...restCamera } = cameraConfig as any
            // Filter out unchecked camera settings (defaults) so only explicitly enabled are sent
            const DEFAULTS: Record<string, number | null> = {
                sensorWidth: null,
                focalDistance: null,
                exposure: null,
            }
            const filteredRestCamera = Object.fromEntries(
                Object.entries(restCamera)
                    .filter(([key, value]) => {
                        // Always include non-filtered keys
                        if (!(key in DEFAULTS)) return true;
                        // Include only if value differs from default
                        return value !== DEFAULTS[key];
                    })
            ) as Record<string, any>
            const cameraPayload = {
                ...filteredRestCamera,
                forward,
                up,
                transform: {
                    ...reduxTransform,
                    position: pos,
                    rotation: rotation,
                },
            }

            if (!templateId || forceLocal) {
                // Save local preset
                const localPresets = getLocalPresets()
                const newPreset: CameraPreset = {
                    id: Date.now().toString(),
                    label: customName || `Preset ${localPresets.length + 1}`,
                    config: cameraPayload,
                }
                const updatedLocalPresets = [...localPresets, newPreset]
                saveLocalPresets(updatedLocalPresets)
                await loadPresets()
                setActivePreset(newPreset)
            } else {
                // Save server preset
                const payload = { templateId, camera: cameraPayload }
                console.log("TEST payload", payload);
                const resp = await studioAxiosInstance.post<any>('cameraPresets', payload, { params: { templateId } })
                const newPreset: CameraPreset = {
                    id: String(resp.data.id),
                    label: resp.data.name,
                    config: {
                        ...resp.data.camera,
                        transform: cameraPayload.transform,
                    },
                }
                console.log("TEST newPreset", newPreset);
                await loadPresets()
                setActivePreset(newPreset)
            }
        } catch (e) {
            console.log('Save preset failed', e)
        }
    }

    return { presets, activePreset, setActivePreset, deletePreset, savePreset, loadPresets }
} 